import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { getCurrentSpaceId } from '@/components/App/selectors'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import Page from '@/components/Page'
import { resetRequest } from '@/store/actions'
import { getNotificationSources, isRequestError, isRequestPending } from '@/store/selectors'

import {
  createNotificationSource,
  deleteNotificationSource,
  fetchNotificationSources,
  updateNotificationSource
} from './actions'
import {
  CREATE_NOTIFICATION_SOURCE,
  DELETE_NOTIFICATION_SOURCE,
  FETCH_NOTIFICATION_SOURCES,
  UPDATE_NOTIFICATION_SOURCE
} from './actionTypes'
import AddNotificationSource from './AddNotificationSource'
import messages from './messages'
import NotificationSources from './NotificationSources'

const SpaceNotificationSourcesPage = ({
  actions,
  currentSpaceId,
  hasError,
  notificationSources,
  isCreateOrUpdateInProgress,
  isFetchOrDeleteInProgress,
  startIndex
}: any) => {
  const intl = useIntl()

  const [currentNotificationSource, setCurrentNotificationSource] = useState({ displayTitle: '', iconUrl: '' })
  const [isEditing, setIsEditing] = useState(false)
  const [deletingNotificationSource, setDeletingNotificationSource] = useState<any>()
  const [localError, setLocalError] = useState()
  // There's an odd issue in the "onEdit" where these notificationSources from props are empty for some reason
  const actualSources = useRef(notificationSources)

  useEffect(() => {
    actions.resetRequest([CREATE_NOTIFICATION_SOURCE, UPDATE_NOTIFICATION_SOURCE, DELETE_NOTIFICATION_SOURCE])

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  useEffect(() => {
    // We need to, for some reason, hold onto a copy of the sources for `onEdit`
    actualSources.current = notificationSources
  }, [notificationSources])

  useEffect(() => {
    actions.fetchNotificationSources()
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentSpaceId])

  const onEdit = (notificationSourceId: any) => {
    // Find the notification source with the id that is passed in

    // Attempting to use the actual prop results in inconsistencies for some reason
    // For example the array will be empty at this point even though redux tools AND the sources table are fine
    const currentNotificationSource = actualSources.current.find(
      (notificationSource: any) => notificationSource.id === notificationSourceId
    )

    if (currentNotificationSource) {
      setCurrentNotificationSource(currentNotificationSource)
      setIsEditing(true)
      setDeletingNotificationSource(undefined)
      window.scrollTo(0, 0)
    }
  }

  const onCreate = async (source: any) => {
    try {
      await actions.createNotificationSource({
        title: source.displayTitle,
        iconUrl: source.iconUrl
      })
      //Re-fetch sources to see the updated source
      await actions.fetchNotificationSources()
    } catch (error) {
      // @ts-expect-error
      setLocalError(error)
    }
  }

  const onUpdate = async (source: any) => {
    try {
      await actions.updateNotificationSource(source.id, {
        title: source.displayTitle,
        iconUrl: source.iconUrl
      })
      //Re-fetch sources to see the updated source
      await actions.fetchNotificationSources()
      setIsEditing(false)
    } catch (error) {
      // @ts-expect-error
      setLocalError(error)
    }
  }

  const onDelete = (source: any) => {
    setDeletingNotificationSource(source)
  }

  const onCancel = () => {
    setCurrentNotificationSource({ displayTitle: '', iconUrl: '' })
    setIsEditing(false)
    setDeletingNotificationSource(undefined)
  }

  const onDeleteConfirm = async () => {
    try {
      await actions.deleteNotificationSource(deletingNotificationSource.id)
      // @ts-expect-error
      if (deletingNotificationSource.id === currentNotificationSource.id) {
        onCancel()
      } else {
        setDeletingNotificationSource(undefined)
      }
      //Re-fetch sources to see the updated source
      await actions.fetchNotificationSources()
    } catch (error) {
      // @ts-expect-error
      setLocalError(error)
    }
  }

  const onDeleteCancel = () => {
    setDeletingNotificationSource(undefined)
  }

  return (
    <Page>
      <Page.Header title={<FormattedMessage {...messages.title} />} />

      {hasError || localError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        <>
          <AddNotificationSource
            notificationSource={currentNotificationSource}
            isActionInProgress={isCreateOrUpdateInProgress}
            isEditing={isEditing}
            onCancel={onCancel}
            onCreate={onCreate}
            onUpdate={onUpdate}
          />

          {!!deletingNotificationSource && (
            <DeleteConfirmationOverlay
              message={
                <FormattedMessage
                  {...messages.deleteConfirm}
                  values={{ notificationSource: deletingNotificationSource.displayTitle }}
                />
              }
              onCancel={onDeleteCancel}
              onConfirm={onDeleteConfirm}
            />
          )}

          <NotificationSources
            notificationSources={notificationSources
              .filter((source: any) => !source.disabled)
              .map((source: any) => {
                return { ...source, iconUrl: source.iconUrl }
              })}
            isActionInProgress={isFetchOrDeleteInProgress}
            spaceId={currentSpaceId}
            onDelete={onDelete}
            onEdit={onEdit}
            perPage={10}
            startIndex={startIndex}
          />
        </>
      )}
    </Page>
  )
}

SpaceNotificationSourcesPage.displayName = 'SpaceNotificationSourcesPage'

SpaceNotificationSourcesPage.propTypes = {
  actions: PropTypes.object.isRequired,
  hasError: PropTypes.bool,
  isCreateOrUpdateInProgress: PropTypes.bool.isRequired,
  isFetchOrDeleteInProgress: PropTypes.bool.isRequired,
  currentSpaceId: PropTypes.string.isRequired,

  notificationSources: PropTypes.array,
  startIndex: PropTypes.number.isRequired
}

SpaceNotificationSourcesPage.defaultProps = {
  hasError: false,
  notificationSources: []
}

function mapStateToProps(state: any) {
  return {
    hasError: isRequestError(state, [
      FETCH_NOTIFICATION_SOURCES,
      CREATE_NOTIFICATION_SOURCE,
      DELETE_NOTIFICATION_SOURCE,
      UPDATE_NOTIFICATION_SOURCE
    ]),
    isCreateOrUpdateInProgress: isRequestPending(state, [CREATE_NOTIFICATION_SOURCE, UPDATE_NOTIFICATION_SOURCE]),
    isFetchOrDeleteInProgress: isRequestPending(state, [FETCH_NOTIFICATION_SOURCES, DELETE_NOTIFICATION_SOURCE]),
    notificationSources: getNotificationSources(state),
    startIndex: 1,
    currentSpaceId: getCurrentSpaceId(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      {
        fetchNotificationSources,
        deleteNotificationSource,
        createNotificationSource,
        updateNotificationSource,
        resetRequest
      },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SpaceNotificationSourcesPage)

const SpaceNotificationSourcesPageSansRedux = SpaceNotificationSourcesPage

export { SpaceNotificationSourcesPageSansRedux } // For Testing
